if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
        this.hasUserInteracted = false; // Track if user has made any selections
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productOptions() {
        if (!this._productOptions) {
          const optionsScript = this.querySelector('[data-product-options]');
          if (optionsScript) {
            try {
              const data = JSON.parse(optionsScript.textContent);
              this._productOptions = data.product;
            } catch (e) {
              console.error('Error parsing product options:', e);
              this._productOptions = null;
            }
          } else {
            this._productOptions = null;
          }
        }
        return this._productOptions;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // Make all variants available on initial load
        this.makeAllVariantsAvailable();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        // Initialize product display
        requestAnimationFrame(() => {
          // Ensure color selections are properly displayed
          this.updateAllSwatchValueDisplays();
          // Validate button state on initial load
          if (!this.checkIfAllOptionsSelected()) {
            this.toggleAddButton(true, this.getMissingOptionMessage());
          } else {
            this.toggleAddButton(false, FoxTheme.variantStrings.addToCart);
          }
        });
      }



      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValue } }) {
        if (!this.contains(event.target)) return;
        
        // Mark that user has interacted
        this.hasUserInteracted = true;
        
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        
        this.renderProductInfoDynamic(selectedOptionValue);
        
        // LEGACY LOGIC
        // Make server request when any selection is made or when swapping products
        // this.renderProductInfo({
        //   requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValue, shouldFetchFullPage),
        //   targetId: target.id,
        //   callback: shouldSwapProduct
        //   ? this.handleSwapProduct(shouldFetchFullPage, viewMode)
        //   : this.handleUpdateProductInfo(viewMode),
        // });
      }

      handleSwapProduct(updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));


          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              if (this.checkIfAllOptionsSelected()) {
                this.setUnavailable();
              } else {
                this.toggleAddButton(true, this.getMissingOptionMessage());
              }
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            if (this.checkIfAllOptionsSelected()) {
              this.setUnavailable();
            } else {
              this.toggleAddButton(true, this.getMissingOptionMessage());
            }
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('price-mobile');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          // Only enable button if ALL options are selected, not just any
          if (!this.checkIfAllOptionsSelected()) {
            this.toggleAddButton(true, this.getMissingOptionMessage());
          } else {
            // All options selected and variant exists - check availability
            if (!variant.available) {
              // Variant exists but not available = sold out
              this.toggleAddButton(true, FoxTheme.variantStrings.soldOut);
            } else {
              // Variant available = enable button
              this.toggleAddButton(false, FoxTheme.variantStrings.addToCart);
            }
          }

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }


      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (!selectedVariant || selectedVariant.trim() === '' || selectedVariant.trim() === 'null') {
          return null;
        }
        try {
          const parsed = JSON.parse(selectedVariant);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
          console.error('Error parsing variant:', e);
          return null;
        }
      }

      renderProductInfoDynamic(selectedOptionValue) {
        // Access product options data
        const productOptions = this.productOptions;

        // Check if any inputs are already selected
        const selectedInputs = this.checkIfAnyInputSelected();

        // If any inputs are selected, update the input availability
        if (selectedInputs.some(input => input.value !== null)) {
          this.updateInputAvailability(selectedInputs);
          // Media is updated inside updateAddToCartButton, no need to call it again here
        } else {
          this.cleanAllSelectedElements();
          // Disable all variants when unselecting for products with only 1 variant option
          this.makeAllVariantsDisabled();
        }

      }

      cleanAllSelectedElements() {
        const selectedInputs = this.variantSelectors.querySelectorAll('input[type="radio"].disabled:not([id*="nested"])');
        selectedInputs.forEach(input => {
          input.checked = false;
          input.classList.remove('disabled');
        });
        const selectedSpans = this.variantSelectors.querySelectorAll('[data-selected-swatch-value]:not([data-selected-swatch-value="Color"])');
        selectedSpans.forEach(span => {
          span.innerHTML = "";
        });
      }

      updateInputAvailability(selectedInputs) {
        if (!this.productOptions || !this.productOptions.variants) {
          return;
        }

        // Step 1: Build available values for each option position
        // For each position, filter variants by selections from OTHER positions only
        const availableValuesByPosition = {};
        
        [1, 2, 3].forEach(targetPosition => {
          availableValuesByPosition[targetPosition] = new Set();
          
          // Filter variants that match selections from OTHER positions
          const matchingVariants = this.productOptions.variants.filter(variant => {
            return selectedInputs.every(selected => {
              // Skip null values (unselected options)
              if (selected.value === null) return true;
              
              // Skip the target position - we're checking what's available FOR this position
              if (selected.position === targetPosition) return true;
              
              // Check if this variant matches the selected value at other positions
              return variant[`option${selected.position}`] === selected.value;
            });
          });
          
          // Add available values for this position
          matchingVariants.forEach(variant => {
            const optionKey = `option${targetPosition}`;
            const value = variant[optionKey];
            
            if (value && variant.available) {
              availableValuesByPosition[targetPosition].add(value);
            }
          });
        });

        // Step 2: Update data-selected-swatch-value spans with selected values
        selectedInputs.forEach(selected => {
          // Find the option name for this position
          const option = this.productOptions.options.find(o => o.position === selected.position);
          
          if (option) {
            // Find the span with data-selected-swatch-value matching the option name
            const swatchValueSpan = this.variantSelectors.querySelector(`[data-selected-swatch-value="${option.name}"]`);
            
            if (swatchValueSpan) {
              if (selected.value !== null) {
                // Update the text content to show the selected value
                swatchValueSpan.textContent = selected.value;
              } else {
                // Clear the text content when value is unselected (null)
                swatchValueSpan.textContent = '';
              }
            }
          }
        });

        // Step 3: Update radio button inputs
        const formRadios = this.variantSelectors.querySelectorAll('input[type="radio"]:not([id*="nested"])');
        formRadios.forEach(radio => {
          // Find which option position this radio belongs to
          const optionPosition = this.getOptionPositionForInput(radio);
          
          if (optionPosition) {
            const availableValues = availableValuesByPosition[optionPosition];
            const isAvailable = availableValues && availableValues.has(radio.value);
            
            // Update radio state with class instead of disabled property
            if (isAvailable) {
              radio.classList.remove('disabled');
            } else {
              radio.classList.add('disabled');
            }
            
            // Update label styling
            const label = radio.closest('label');
            if (label) {
              if (isAvailable) {
                label.classList.remove('unavailable', 'disabled');
                label.removeAttribute('data-sold-out');
              } else {
                label.classList.add('unavailable', 'disabled');
                label.setAttribute('data-sold-out', 'true');
              }
            }
          }
        });

        // Step 4: Update select dropdown options
        const formSelects = this.variantSelectors.querySelectorAll('select');
        formSelects.forEach(select => {
          const optionPosition = this.getOptionPositionForInput(select);
          
          if (optionPosition) {
            const availableValues = availableValuesByPosition[optionPosition];
            
            Array.from(select.options).forEach(option => {
              if (option.value) {
                const isAvailable = availableValues && availableValues.has(option.value);
                
                // Update option state with class instead of disabled property
                if (isAvailable) {
                  option.classList.remove('unavailable', 'disabled');
                } else {
                  option.classList.add('unavailable', 'disabled');
                }
              }
            });
          }
        });

        // Step 5: Update Add to Cart button state based on variant availability
        this.updateAddToCartButton(selectedInputs);
      }

      updateAddToCartButton(selectedInputs) {
        // Check if all options are selected (no null values)
        const allOptionsSelected = selectedInputs.every(input => input.value !== null);
        
        // Try to find a matching variant even with partial selections (for media update)
        const partialMatchingVariant = this.productOptions.variants.find(variant => {
          return selectedInputs.every(selected => {
            // Skip null values for partial matching
            if (selected.value === null) return true;
            return variant[`option${selected.position}`] === selected.value;
          });
        });
        
        // Update media gallery if we have a partial match (e.g., color selected but not size)
        if (partialMatchingVariant) {
          this.updateMedia(partialMatchingVariant);
          this.updateShareUrl(partialMatchingVariant.id);
        }
        
        if (!allOptionsSelected) {
          // Not all options selected - disable ATC button and clear variant ID
          this.updateVariantIdInput(null, true);
          this.toggleAddButton(true, this.getMissingOptionMessage());
          this.currentVariant = null;
          return;
        }

        // All options selected - find the exact matching variant
        const matchingVariant = this.productOptions.variants.find(variant => {
          return selectedInputs.every(selected => {
            return variant[`option${selected.position}`] === selected.value;
          });
        });

        if (!matchingVariant) {
          // No matching variant found
          this.updateVariantIdInput(null, true);
          this.toggleAddButton(true, FoxTheme.variantStrings.unavailable || 'Unavailable');
          this.currentVariant = null;
          return;
        }

        // Update current variant and media gallery
        this.currentVariant = matchingVariant;
        this.updateMedia(matchingVariant);
        this.updateShareUrl(matchingVariant.id);
        this.pickupAvailability?.update(matchingVariant);

        if (!matchingVariant.available) {
          // Variant exists but not available
          this.updateVariantIdInput(matchingVariant.id, true);
          this.toggleAddButton(true, FoxTheme.variantStrings.soldOut || 'Sold out');
          return;
        }

        // Variant is available - enable ATC button and update variant ID
        this.updateVariantIdInput(matchingVariant.id, false);
        this.toggleAddButton(false);
        
        // Publish variant change event
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant: matchingVariant,
          },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: matchingVariant,
            },
          })
        );
      }

      updateVariantIdInput(variantId, disabled) {
        // Find all hidden inputs with class "product-variant-id" in the current product-info context
        const variantIdInputs = this.querySelectorAll('input.product-variant-id');
        
        variantIdInputs.forEach(input => {
          if (variantId) {
            input.value = variantId;
          } else {
            input.value = '';
          }
          
          // Enable or disable the input based on variant availability
          if (disabled) {
            input.setAttribute('disabled', 'disabled');
          } else {
            input.removeAttribute('disabled');
          }
        });
      }

      getOptionPositionForInput(input) {
        // Match input to option position by checking productOptions
        if (!this.productOptions || !this.productOptions.options) {
          return null;
        }

        // Try to find by matching input value to option values
        for (let i = 0; i < this.productOptions.options.length; i++) {
          const option = this.productOptions.options[i];
          
          // For radio buttons
          if (input.type === 'radio' && option.values.includes(input.value)) {
            return option.position;
          }
          
          // For select dropdowns
          if (input.tagName === 'SELECT') {
            const hasMatchingValue = Array.from(input.options).some(opt => 
              option.values.includes(opt.value)
            );
            if (hasMatchingValue) {
              return option.position;
            }
          }
        }
        
        return null;
      }

      checkIfAnyInputSelected() {
        if (!this.variantSelectors) {
          return { hasSelection: false, selected: [] };
        }

        const selectedInputs = [];

        // Check radio buttons
        const checkedRadios = this.variantSelectors.querySelectorAll('input[type="radio"]:checked:not([id*="nested"])');
        checkedRadios.forEach(radio => {
          selectedInputs.push({
            type: 'radio',
            name: radio.name,
            value: radio.value,
            element: radio
          });
        });

        // Check select dropdowns
        const selects = this.variantSelectors.querySelectorAll('select');
        selects.forEach(select => {
          if (select.value && select.value !== '') {
            selectedInputs.push({
              type: 'select',
              name: select.name,
              value: select.value,
              selectedOption: select.selectedOptions[0]?.textContent,
              element: select
            });
          }
        });

        // Check checkboxes (if any)
        const checkedCheckboxes = this.variantSelectors.querySelectorAll('input[type="checkbox"]:checked');
        checkedCheckboxes.forEach(checkbox => {
          selectedInputs.push({
            type: 'checkbox',
            name: checkbox.name,
            value: checkbox.value,
            element: checkbox
          });
        });

        // Creates an array of objects with the position and value of the matched option
        const matches = this.productOptions.options.map((o, i) => {
          const matched = o.values.find(v => selectedInputs.some(s => s.value === v));
          return { position: i+1, name: o.name, value: matched || null };
        });

        return matches;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            // For select elements, we need to find the option element since it has the ID
            const option = document.querySelector(`#${targetId}`);
            if (option?.tagName === 'OPTION') {
              option.parentElement?.focus(); // Focus the select element instead
            } else {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            } else {
              console.error(error);
            }
          });
      }

      checkIfAllOptionsSelected() {
        // Check if all required options have been selected
        const allSelects = this.variantSelectors.querySelectorAll('select');
        const allFieldsets = this.variantSelectors.querySelectorAll('fieldset');
        
        // Check selects
        for (const select of allSelects) {
          if (!select.selectedOptions[0] || select.selectedOptions[0].value === '') {
            return false;
          }
        }
        
        // Check radio button fieldsets
        for (const fieldset of allFieldsets) {
          const checkedInput = fieldset.querySelector('input:checked');
          if (!checkedInput) {
            return false;
          }
        }
        
        return true;
      }

      checkIfAnyOptionsSelected() {
        // Check if at least one option has been selected
        const allSelects = this.variantSelectors.querySelectorAll('select');
        const allFieldsets = this.variantSelectors.querySelectorAll('fieldset');
        
        // Check if any select has a selection
        for (const select of allSelects) {
          if (select.selectedOptions[0] && select.selectedOptions[0].value !== '') {
            return true;
          }
        }
        
        // Check if any fieldset has a checked input
        for (const fieldset of allFieldsets) {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) {
            return true;
          }
        }
        
        return false;
      }

      getMissingOptionMessage() {
        const selects = this.variantSelectors.querySelectorAll('select');
        for (const select of selects) {
          if (!select.selectedOptions[0] || select.selectedOptions[0].value === '') {
            let optionName = select.name || select.closest('.product-form__input')?.querySelector('label')?.childNodes[0]?.textContent?.trim();
            optionName = optionName?.replace(/[-_]\d+/g, '').trim();
            return `Choose ${optionName || 'Options'}`;
          }
        }
        const fieldsets = this.variantSelectors.querySelectorAll('fieldset');
        for (const fieldset of fieldsets) {
          if (!fieldset.querySelector('input:checked')) {
            const input = fieldset.querySelector('input');
            let optionName = input?.name || fieldset.querySelector('legend')?.childNodes[0]?.textContent?.trim()?.replace('*', '').trim();
            optionName = optionName?.replace(/[-_]\d+/g, '').trim();
            return `Choose ${optionName || 'Options'}`;
          }
        }
        return 'Choose Options';
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );

          // Update the dropdown text display for color variants
          const selectedValueDisplay = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value]');
          if (selectedValueDisplay) {
            const selectedOption = target.selectedOptions[0];
            // Use optimized color detection for select options
            const colorName = this.getColorDisplayValue(selectedOption);
            selectedValueDisplay.textContent = colorName || selectedOption.textContent;
          }
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-swatch-value]');
          if (selectedSwatchValue) {
            // Use optimized color detection and extraction
            const colorName = this.getColorDisplayValue(target);
            selectedSwatchValue.innerHTML = colorName || value;
          }
        }
      }

      getColorDisplayValue(element) {
        if (!element) return null;
        
        // Quick check - if has swatch data attributes, it's a color
        if (element.hasAttribute('data-product-value')) {
          return element.getAttribute('data-product-value');
        }
        
        // Check if it's a color variant by looking for swatch indicators
        const label = element.closest('label') || element.closest('.product-form__input')?.querySelector(`label[for="${element.id}"]`);
        const isColor = element.hasAttribute('data-option-swatch-value') ||
                       label?.querySelector('.swatch, .swatch-color, .tooltip') ||
                       label?.hasAttribute('data-swatch-type') ||
                       element.closest('fieldset')?.hasAttribute('data-color') ||
                       element.name?.toLowerCase().includes('color');
        
        if (!isColor) return null;
        
        // Try to get color name in priority order
        return element.getAttribute('data-color-name') ||
               label?.querySelector('.tooltip')?.textContent?.trim() ||
               label?.getAttribute('data-value') ||
               this.getCleanLabelText(label) ||
               (this.isValidColorName(element.value) ? element.value : null);
      }

      getCleanLabelText(label) {
        if (!label) return null;
        const text = label.textContent.trim();
        const swatch = label.querySelector('.swatch, .swatch-color');
        return swatch ? text.replace(swatch.textContent || '', '').trim() || null : text;
      }

      isValidColorName(text) {
        return text && !/^\d+$/.test(text.trim()) && text.trim().length < 50;
      }

      makeAllVariantsAvailable() {
        // Only run for products with more than 1 variant option (e.g., Color AND Size)
        if (!this.productOptions?.options || this.productOptions.options.length <= 1) {
          return;
        }
        
        // Store current selections before making changes
        const currentSelections = this.getCurrentUserSelections();
        
        // Make all select options available
        const selects = this.variantSelectors?.querySelectorAll('select') || [];
        selects.forEach(select => {
          Array.from(select.options).forEach(option => {
            option.disabled = false;
            option.setAttribute('data-available', 'true');
            option.classList.remove('disabled', 'sold-out', 'unavailable');
          });
        });

        // Make all radio button options available
        const radioInputs = this.variantSelectors?.querySelectorAll('input[type="radio"]') || [];
        radioInputs.forEach(input => {
          input.disabled = false;
          input.setAttribute('data-available', 'true');
          input.classList.remove('disabled', 'sold-out', 'unavailable');
          
          // Update associated labels
          const label = input.closest('label') || 
                       input.closest('.product-form__input')?.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.remove('disabled', 'sold-out', 'unavailable');
            label.removeAttribute('data-sold-out');
          }
        });
        
        // Restore any existing selections (including colors)
        this.restoreUserSelections(currentSelections);
        
        // Also preserve any pre-selected options that weren't captured above
        this.preservePreSelectedOptions();
      }

      makeAllVariantsDisabled() {
        // Only run for products with 1 variant option
        if (!this.productOptions?.options || this.productOptions.options.length !== 1) {
          return;
        }

        // Only disable if ALL variants are sold out
        const allVariantsSoldOut = this.productOptions.variants.every(variant => !variant.available);
        if (!allVariantsSoldOut) {
          return;
        }

        // Make all non-swatch select options disabled
        const selects = this.variantSelectors?.querySelectorAll('select') || [];
        selects.forEach(select => {
          // Skip swatch/color selects
          const isSwatchSelect = select.closest('[data-swatch-type]') || select.closest('[data-color="true"]');
          if (isSwatchSelect) return;

          Array.from(select.options).forEach(option => {
            option.disabled = true;
            option.setAttribute('data-available', 'false');
            option.classList.add('disabled', 'unavailable');
          });
        });

        // Make all non-swatch radio button options disabled
        const radioInputs = this.variantSelectors?.querySelectorAll('input[type="radio"]') || [];
        radioInputs.forEach(input => {
          // Skip swatch inputs
          const isSwatchInput = input.closest('[data-swatch-type]') || 
                               input.closest('[data-color="true"]') ||
                               input.classList.contains('swatch-input__input');
          if (isSwatchInput) return;

          input.disabled = true;
          input.setAttribute('data-available', 'false');
          input.classList.add('disabled', 'unavailable');
          
          // Update associated labels
          const label = input.closest('label') || 
                       input.closest('.product-form__input')?.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.add('disabled', 'unavailable');
          }
        });
      }

      preservePreSelectedOptions() {
        // Preserve any selected options that were set by default or from URL
        const selectedOptions = this.variantSelectors?.querySelectorAll('option[selected]') || [];
        selectedOptions.forEach(option => {
          if (option.parentElement && option.parentElement.tagName === 'SELECT') {
            option.parentElement.value = option.value;
          }
        });
        
        // Preserve any checked radio buttons
        const checkedRadios = this.variantSelectors?.querySelectorAll('input[type="radio"][checked]') || [];
        checkedRadios.forEach(radio => {
          radio.checked = true;
        });
        
        // Update swatch displays for preserved selections
        this.updateAllSwatchValueDisplays();
      }

      updateOptionValues(html, skipAvailabilityUpdate = false) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // Only update the data-selected-variant script tag, not the entire variant picker
          const newSelectedVariantScript = html.querySelector('variant-selects [data-selected-variant]');
          const currentSelectedVariantScript = this.variantSelectors.querySelector('[data-selected-variant]');
          
          if (newSelectedVariantScript && currentSelectedVariantScript) {
            currentSelectedVariantScript.innerHTML = newSelectedVariantScript.innerHTML;
          }
          
          // Update option availability
          if (!skipAvailabilityUpdate) {
            this.updateOptionAvailability(variantSelects);
          }
        }
      }

      updateOptionAvailability(newVariantSelects) {
        // If user hasn't interacted yet, keep all variants available
        if (!this.hasUserInteracted) {
          return;
        }
        
        // Store all current user selections before making changes
        const currentSelections = this.getCurrentUserSelections();
        
        // Update availability states for select elements
        const currentSelects = this.variantSelectors.querySelectorAll('select');
        const newSelects = newVariantSelects.querySelectorAll('select');
        
        currentSelects.forEach((currentSelect, index) => {
          const newSelect = newSelects[index];
          if (!newSelect) return;
          
          // Update options availability and copy all relevant attributes
          Array.from(currentSelect.options).forEach((currentOption, optionIndex) => {
            const newOption = newSelect.options[optionIndex];
            if (newOption) {
              // Copy availability states
              currentOption.disabled = newOption.disabled;
              
              // Copy all data attributes that might affect availability/styling
              ['data-available', 'data-option-swatch-value', 'data-option-swatch-focal-point'].forEach(attr => {
                if (newOption.hasAttribute(attr)) {
                  currentOption.setAttribute(attr, newOption.getAttribute(attr));
                } else {
                  currentOption.removeAttribute(attr);
                }
              });
              
              // Copy classes that might indicate availability
              currentOption.className = newOption.className;
            }
          });
        });

        // Update availability states for radio button fieldsets
        const currentFieldsets = this.variantSelectors.querySelectorAll('fieldset');
        const newFieldsets = newVariantSelects.querySelectorAll('fieldset');
        
        currentFieldsets.forEach((currentFieldset, index) => {
          const newFieldset = newFieldsets[index];
          if (!newFieldset) return;
          
          // Update radio button availability
          const currentInputs = currentFieldset.querySelectorAll('input[type="radio"]');
          const newInputs = newFieldset.querySelectorAll('input[type="radio"]');
          
          currentInputs.forEach((currentInput, inputIndex) => {
            const newInput = newInputs[inputIndex];
            if (newInput) {
              // Copy availability states
              currentInput.disabled = newInput.disabled;
              
              // Copy all relevant data attributes
              ['data-available', 'data-option-swatch-value', 'data-option-swatch-focal-point'].forEach(attr => {
                if (newInput.hasAttribute(attr)) {
                  currentInput.setAttribute(attr, newInput.getAttribute(attr));
                } else {
                  currentInput.removeAttribute(attr);
                }
              });
              
              // Copy classes
              currentInput.className = newInput.className;
              
              // Update the label styling and all attributes
              const currentLabel = currentInput.closest('label') || currentFieldset.querySelector(`label[for="${currentInput.id}"]`);
              const newLabel = newInput.closest('label') || newFieldset.querySelector(`label[for="${newInput.id}"]`);
              
              if (currentLabel && newLabel) {
                // Copy all classes from new label to current label
                currentLabel.className = newLabel.className;
                
                // Copy swatch styling elements
                const currentSwatch = currentLabel.querySelector('.swatch');
                const newSwatch = newLabel.querySelector('.swatch');
                
                if (currentSwatch && newSwatch) {
                  // Copy swatch classes and styles
                  currentSwatch.className = newSwatch.className;
                  currentSwatch.style.cssText = newSwatch.style.cssText;
                  
                  // Copy any data attributes on swatch
                  Array.from(newSwatch.attributes).forEach(attr => {
                    if (attr.name.startsWith('data-')) {
                      currentSwatch.setAttribute(attr.name, attr.value);
                    }
                  });
                }
                
                // Copy any other child elements that might show availability state
                const currentSpans = currentLabel.querySelectorAll('span:not(.swatch)');
                const newSpans = newLabel.querySelectorAll('span:not(.swatch)');
                
                currentSpans.forEach((currentSpan, spanIndex) => {
                  const newSpan = newSpans[spanIndex];
                  if (newSpan) {
                    currentSpan.className = newSpan.className;
                    currentSpan.textContent = newSpan.textContent;
                  }
                });
              }
            }
          });
        });
        
        // Now restore only the user's actual selections
        this.restoreUserSelections(currentSelections);
        
        // Update all swatch value displays to reflect current selections
        this.updateAllSwatchValueDisplays();
      }

      getCurrentUserSelections() {
        const selections = {};
        
        // Store select element selections
        const selects = this.variantSelectors.querySelectorAll('select');
        selects.forEach((select, index) => {
          if (select.value && select.value !== '') {
            selections[`select_${index}`] = select.value;
          }
        });
        
        // Store radio button selections
        const fieldsets = this.variantSelectors.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset, index) => {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) {
            selections[`fieldset_${index}`] = checkedInput.value;
          }
        });
        
        return selections;
      }

      restoreUserSelections(selections) {
        // Restore select element selections
        const selects = this.variantSelectors.querySelectorAll('select');
        selects.forEach((select, index) => {
          const savedValue = selections[`select_${index}`];
          if (savedValue) {
            const targetOption = select.querySelector(`option[value="${savedValue}"]`);
            if (targetOption && !targetOption.disabled) {
              select.value = savedValue;
              targetOption.setAttribute('selected', 'selected');
              this.updateSelectedSwatchValue({ target: select });
            }
          }
        });
        
        // Restore radio button selections
        const fieldsets = this.variantSelectors.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset, index) => {
          const savedValue = selections[`fieldset_${index}`];
          if (savedValue) {
            const targetInput = fieldset.querySelector(`input[value="${savedValue}"]`);
            if (targetInput && !targetInput.disabled) {
              targetInput.checked = true;
              this.updateSelectedSwatchValue({ target: targetInput });
            }
          }
        });
      }

      updateAllSwatchValueDisplays() {
        // Update displays for all selected values
        
        // Handle select elements
        const selects = this.variantSelectors.querySelectorAll('select');
        selects.forEach(select => {
          if (select.value && select.value !== '') {
            this.updateSelectedSwatchValue({ target: select });
          }
        });
        
        // Handle radio button inputs
        const checkedRadios = this.variantSelectors.querySelectorAll('input[type="radio"]:checked');
        checkedRadios.forEach(radio => {
          this.updateSelectedSwatchValue({ target: radio });
        });
        
        // Also update any data-selected-swatch-value elements that might have been affected
        const swatchValueElements = this.variantSelectors.querySelectorAll('[data-selected-swatch-value]');
        swatchValueElements.forEach(element => {
          const parentInput = element.closest('.product-form__input');
          if (parentInput) {
            const checkedRadio = parentInput.querySelector('input[type="radio"]:checked');
            if (checkedRadio) {
              const colorName = this.getColorDisplayValue(checkedRadio);
              element.innerHTML = colorName || checkedRadio.value;
            }
          }
        });
        
        // Update any data-selected-value displays for dropdowns
        const selectedValueElements = this.variantSelectors.querySelectorAll('[data-selected-value]');
        selectedValueElements.forEach(element => {
          const parentInput = element.closest('.product-form__input');
          if (parentInput) {
            const select = parentInput.querySelector('select');
            if (select && select.selectedOptions.length) {
              const selectedOption = select.selectedOptions[0];
              const colorName = this.getColorDisplayValue(selectedOption);
              element.textContent = colorName || selectedOption.textContent;
            }
          }
        });
      }


      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        if (!variant) return;
        
        // Find MediaGallery - try with section ID first, then any MediaGallery
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`) ||
                           document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`) ||
                           document.querySelector(`[id^="MediaGallery"]`);
        
        if (!productMedia) return;

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            try {
              productMedia.init();
              productMedia.setActiveMedia(variant);
              return true; // Indicate success
            } catch (error) {
              console.error('Error setting active media:', error);
              return false;
            }
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          // Clear any existing timer first
          if (this.timer) {
            clearInterval(this.timer);
          }
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

    }
  );
}
